import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import Modal from '../../../shared/components/Modal';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import {
  createStandardRatioDefinition,
  fetchStandardRatioDefinitions,
  updateStandardRatioDefinition
} from '../../../services/standardRatioDefinitions';
import './StandardRatioDefinitionsPage.css';

const STATUS_OPTIONS = ['Aktif', 'Pasif'];

const UI_TEXT = {
  'tr-TR': {
    pageTitle: 'Standart Rasyolar',
    pageDescription: 'Standart rasyo tanimlari bu modulde yonetilir ve analysis/ratios-standard tarafinda kullanilir.',
    accessDeniedDescription: 'Bu modula sadece SYS_ADMIN rolundeki kullanicilar erisebilir.',
    newDefinition: 'Yeni Tanim',
    key: 'Anahtar',
    title: 'Baslik',
    formula: 'Formul',
    standard: 'Standart',
    status: 'Durum',
    action: 'Aksiyon',
    loading: 'Yukleniyor...',
    noRecords: 'Henuz standart rasyo tanimi bulunamadi.',
    edit: 'Duzenle',
    deactivate: 'Pasif Yap',
    activate: 'Aktif Yap',
    close: 'Kapat',
    save: 'Kaydet',
    update: 'Guncelle',
    cancel: 'Iptal',
    saving: 'Kaydediliyor...',
    active: 'Aktif',
    passive: 'Pasif',
    editAria: 'Tanimi duzenle',
    createAria: 'Yeni standart rasyo tanimi',
    editTitle: 'Tanimi duzenle',
    createTitle: 'Yeni standart rasyo tanimi',
    unit: 'Birim',
    standardValue: 'Standart Deger',
    higherIsBetter: 'Yuksek Iyi mi?',
    yes: 'Evet',
    no: 'Hayir',
    systemAliases: 'Sistem Aliaslari',
    sortOrder: 'Siralama',
    notes: 'Not',
    requiredError: 'Anahtar, baslik ve formul zorunludur.',
    invalidStandardValue: 'Standart deger gecerli bir sayi olmalidir.',
    listLoadError: 'Standart rasyo tanimlari yuklenemedi.',
    operationFailed: 'Islem basarisiz oldu.',
    updated: 'Standart rasyo tanimi guncellendi.',
    created: 'Yeni standart rasyo tanimi olusturuldu.',
    statusUpdated: 'Durum guncellendi.',
    statusUpdateError: 'Durum guncellenemedi.',
    notesPlaceholder: 'Opsiyonel not'
  },
  'en-US': {
    pageTitle: 'Standard Ratios',
    pageDescription: 'Standard ratio definitions are managed in this module and used by analysis/ratios-standard.',
    accessDeniedDescription: 'Only users with SYS_ADMIN role can access this module.',
    newDefinition: 'New Definition',
    key: 'Key',
    title: 'Title',
    formula: 'Formula',
    standard: 'Standard',
    status: 'Status',
    action: 'Action',
    loading: 'Loading...',
    noRecords: 'No standard ratio definition found yet.',
    edit: 'Edit',
    deactivate: 'Deactivate',
    activate: 'Activate',
    close: 'Close',
    save: 'Save',
    update: 'Update',
    cancel: 'Cancel',
    saving: 'Saving...',
    active: 'Active',
    passive: 'Passive',
    editAria: 'Edit definition',
    createAria: 'New standard ratio definition',
    editTitle: 'Edit definition',
    createTitle: 'New standard ratio definition',
    unit: 'Unit',
    standardValue: 'Standard Value',
    higherIsBetter: 'Higher is Better?',
    yes: 'Yes',
    no: 'No',
    systemAliases: 'System Aliases',
    sortOrder: 'Sort Order',
    notes: 'Notes',
    requiredError: 'Key, title, and formula are required.',
    invalidStandardValue: 'Standard value must be a valid number.',
    listLoadError: 'Standard ratio definitions could not be loaded.',
    operationFailed: 'Operation failed.',
    updated: 'Standard ratio definition updated.',
    created: 'New standard ratio definition created.',
    statusUpdated: 'Status updated.',
    statusUpdateError: 'Status could not be updated.',
    notesPlaceholder: 'Optional note'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const createEmptyForm = () => ({
  id: null,
  ratioKey: '',
  title: '',
  formula: '',
  unit: '%',
  standardValue: '',
  higherIsBetter: true,
  systemAliases: '',
  sortOrder: 0,
  notes: '',
  status: 'Aktif'
});

const toListString = (values = []) => values.join(', ');

const parseListInput = (input) =>
  input
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

const normalizeRoleName = (rawRole) => {
  const raw = (rawRole || '').trim();
  if (!raw) return '';
  const canonical = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (canonical === 'SYSADMIN') return 'SYS_ADMIN';
  if (canonical === 'TENANTADMIN') return 'TENANT_ADMIN';
  if (canonical === 'TENANTUSER') return 'TENANT_USER';
  return raw.toUpperCase();
};

function StandardRatioDefinitionsPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formState, setFormState] = useState(createEmptyForm());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [listError, setListError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');

  const isSysAdmin = normalizeRoleName(sessionStorage.getItem('authRole')) === 'SYS_ADMIN';
  const isEditMode = Boolean(formState.id);
  const statusLabel = useCallback((value) => (value === 'Aktif' ? t('active') : t('passive')), [t]);

  const loadDefinitions = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const data = await fetchStandardRatioDefinitions();
      setDefinitions(Array.isArray(data) ? data : []);
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : t('listLoadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (isSysAdmin) loadDefinitions();
  }, [isSysAdmin, loadDefinitions]);

  const resetForm = useCallback(() => {
    setFormState(createEmptyForm());
    setFormError('');
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    resetForm();
  }, [resetForm]);

  const openCreateModal = () => {
    resetForm();
    setSuccess('');
    setIsModalOpen(true);
  };

  const preparedPayload = useMemo(
    () => ({
      ratioKey: formState.ratioKey.trim(),
      title: formState.title.trim(),
      formula: formState.formula.trim(),
      unit: formState.unit.trim(),
      standardValue: Number(formState.standardValue),
      higherIsBetter: Boolean(formState.higherIsBetter),
      systemAliases: parseListInput(formState.systemAliases),
      sortOrder: Number(formState.sortOrder),
      notes: formState.notes.trim(),
      status: formState.status
    }),
    [formState]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError('');
    setSuccess('');

    const payload = preparedPayload;
    if (!payload.ratioKey || !payload.title || !payload.formula) {
      setFormError(t('requiredError'));
      setSaving(false);
      return;
    }
    if (!Number.isFinite(payload.standardValue)) {
      setFormError(t('invalidStandardValue'));
      setSaving(false);
      return;
    }

    try {
      if (isEditMode) {
        await updateStandardRatioDefinition(formState.id, payload);
        setSuccess(t('updated'));
      } else {
        await createStandardRatioDefinition(payload);
        setSuccess(t('created'));
      }
      closeModal();
      await loadDefinitions();
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : t('operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (definition) => {
    setFormState({
      id: definition.id,
      ratioKey: definition.ratioKey || '',
      title: definition.title || '',
      formula: definition.formula || '',
      unit: definition.unit || '',
      standardValue: definition.standardValue ?? '',
      higherIsBetter: Boolean(definition.higherIsBetter),
      systemAliases: toListString(definition.systemAliases),
      sortOrder: definition.sortOrder ?? 0,
      notes: definition.notes || '',
      status: definition.status || 'Aktif'
    });
    setFormError('');
    setSuccess('');
    setIsModalOpen(true);
  };

  const handleToggleStatus = async (definition) => {
    setSaving(true);
    setListError('');
    setFormError('');
    setSuccess('');
    try {
      const payload = {
        ratioKey: definition.ratioKey || '',
        title: definition.title || '',
        formula: definition.formula || '',
        unit: definition.unit || '',
        standardValue: Number(definition.standardValue ?? 0),
        higherIsBetter: Boolean(definition.higherIsBetter),
        systemAliases: definition.systemAliases || [],
        sortOrder: Number(definition.sortOrder ?? 0),
        notes: definition.notes || '',
        status: definition.status === 'Aktif' ? 'Pasif' : 'Aktif'
      };
      await updateStandardRatioDefinition(definition.id, payload);
      setSuccess(t('statusUpdated'));
      if (formState.id === definition.id) closeModal();
      await loadDefinitions();
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : t('statusUpdateError'));
    } finally {
      setSaving(false);
    }
  };

  if (!isSysAdmin) {
    return (
      <div className="card standard-ratio-definitions-card">
        <PageHeader title={t('pageTitle')} description={t('accessDeniedDescription')} />
      </div>
    );
  }

  return (
    <div className="card standard-ratio-definitions-card">
      <PageHeader
        title={t('pageTitle')}
        description={t('pageDescription')}
        actions={(
          <button type="button" className="btn-secondary" onClick={openCreateModal}>{t('newDefinition')}</button>
        )}
      />
      {success && <div className="alert alert-success">{success}</div>}

      <div className="standard-ratio-definitions-grid">
        <section className="standard-ratio-definitions-table-panel">
          <div className="table-wrapper">
            <table className="standard-ratio-definitions-table">
              <thead>
                <tr>
                  <th>{t('key')}</th>
                  <th>{t('title')}</th>
                  <th>{t('formula')}</th>
                  <th>{t('standard')}</th>
                  <th>{t('status')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="table-placeholder">{t('loading')}</td></tr>
                ) : listError ? (
                  <tr><td colSpan={6} className="table-placeholder">{listError}</td></tr>
                ) : definitions.length === 0 ? (
                  <tr><td colSpan={6} className="table-placeholder">{t('noRecords')}</td></tr>
                ) : (
                  definitions.map((definition) => (
                    <tr key={definition.id ?? definition.ratioKey}>
                      <td>{definition.ratioKey}</td>
                      <td>{definition.title}</td>
                      <td><span className="formula">{definition.formula}</span></td>
                      <td>{definition.standardValue}</td>
                      <td>
                        <span className={`badge ${definition.status === 'Aktif' ? 'badge-active' : ''}`}>{statusLabel(definition.status)}</span>
                      </td>
                      <td className="action-cell">
                        <button
                          type="button"
                          className="action-icon-btn action-icon-btn-edit"
                          onClick={() => handleEdit(definition)}
                          aria-label={t('edit')}
                          title={t('edit')}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25Zm17.71-10.04a1.003 1.003 0 0 0 0-1.42L18.2 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 2.01-1.66Z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className={`status-toggle-btn ${definition.status === 'Aktif' ? 'status-toggle-btn-deactivate' : 'status-toggle-btn-activate'}`}
                          onClick={() => handleToggleStatus(definition)}
                          title={definition.status === 'Aktif' ? t('deactivate') : t('activate')}
                          aria-label={definition.status === 'Aktif' ? t('deactivate') : t('activate')}
                        >
                          {definition.status === 'Aktif' ? 'P' : 'A'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={isEditMode ? t('editTitle') : t('createTitle')} width="560px">
        <section>
          {formError && <div className="alert alert-error">{formError}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-field">
              <label htmlFor="ratioKey">{t('key')}</label>
              <input
                id="ratioKey"
                name="ratioKey"
                value={formState.ratioKey}
                onChange={(event) => setFormState((current) => ({ ...current, ratioKey: event.target.value }))}
                placeholder="return_on_equity"
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="title">{t('title')}</label>
              <input
                id="title"
                name="title"
                value={formState.title}
                onChange={(event) => setFormState((current) => ({ ...current, title: event.target.value }))}
                placeholder="Ozkaynak Karliligi (ROE)"
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="formula">{t('formula')}</label>
              <input
                id="formula"
                name="formula"
                value={formState.formula}
                onChange={(event) => setFormState((current) => ({ ...current, formula: event.target.value }))}
                placeholder="abs(pl.net_profit) / bs.equity"
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="unit">{t('unit')}</label>
              <input
                id="unit"
                name="unit"
                value={formState.unit}
                onChange={(event) => setFormState((current) => ({ ...current, unit: event.target.value }))}
                placeholder="%"
              />
            </div>
            <div className="form-field">
              <label htmlFor="standardValue">{t('standardValue')}</label>
              <input
                id="standardValue"
                name="standardValue"
                type="number"
                step="0.000001"
                value={formState.standardValue}
                onChange={(event) => setFormState((current) => ({ ...current, standardValue: event.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="higherIsBetter">{t('higherIsBetter')}</label>
              <select
                id="higherIsBetter"
                name="higherIsBetter"
                value={formState.higherIsBetter ? 'true' : 'false'}
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    higherIsBetter: event.target.value === 'true'
                  }))
                }
              >
                <option value="true">{t('yes')}</option>
                <option value="false">{t('no')}</option>
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="systemAliases">{t('systemAliases')}</label>
              <input
                id="systemAliases"
                name="systemAliases"
                value={formState.systemAliases}
                onChange={(event) => setFormState((current) => ({ ...current, systemAliases: event.target.value }))}
                placeholder="ROE, ROA"
              />
            </div>
            <div className="form-field">
              <label htmlFor="sortOrder">{t('sortOrder')}</label>
              <input
                id="sortOrder"
                name="sortOrder"
                type="number"
                min="0"
                value={formState.sortOrder}
                onChange={(event) => setFormState((current) => ({ ...current, sortOrder: Number(event.target.value) }))}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="notes">{t('notes')}</label>
              <textarea
                id="notes"
                name="notes"
                value={formState.notes}
                onChange={(event) => setFormState((current) => ({ ...current, notes: event.target.value }))}
                placeholder={t('notesPlaceholder')}
              />
            </div>
            <div className="form-field">
              <label htmlFor="status">{t('status')}</label>
              <select
                id="status"
                name="status"
                value={formState.status}
                onChange={(event) => setFormState((current) => ({ ...current, status: event.target.value }))}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>{statusLabel(option)}</option>
                ))}
              </select>
            </div>
            <div className="form-footer">
              <button type="submit" className="btn-primary" disabled={saving}>{saving ? t('saving') : isEditMode ? t('update') : t('save')}</button>
              <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>{t('cancel')}</button>
            </div>
          </form>
        </section>
      </Modal>
    </div>
  );
}

export default StandardRatioDefinitionsPage;
